import React from 'react';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';

/**
 * Usage Widget Component
 * Displays usage statistics with progress bars
 *
 * @param {Object} usageStats - Usage statistics data
 */
const UsageWidget = ({ usageStats }) => {
    if (!usageStats || !usageStats.features) {
        return (
            <Card title="Usage This Month">
                <p className="text-600">No usage data available</p>
            </Card>
        );
    }

    const features = Object.entries(usageStats.features);

    if (features.length === 0) {
        return (
            <Card title="Usage This Month">
                <p className="text-600">No feature usage recorded yet</p>
            </Card>
        );
    }

    const formatFeatureName = (name) => {
        return name
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const calculatePercentage = (used, limit) => {
        if (limit === null || limit === undefined) return 0; // Unlimited
        if (limit === 0) return 100;
        return Math.min((used / limit) * 100, 100);
    };

    const getProgressBarColor = (percentage) => {
        if (percentage >= 90) return 'danger';
        if (percentage >= 70) return 'warning';
        return 'success';
    };

    return (
        <Card title="Usage This Month">
            <div className="flex flex-column gap-4">
                {features.slice(0, 5).map(([featureName, stats], index) => {
                    const percentage = calculatePercentage(stats.used || 0, stats.limit);
                    const severity = getProgressBarColor(percentage);

                    return (
                        <div key={index}>
                            <div className="flex justify-content-between mb-2">
                                <span className="text-sm font-medium">{formatFeatureName(featureName)}</span>
                                <span className="text-sm font-semibold">
                                    {stats.used || 0} / {stats.limit === null ? '∞' : stats.limit}
                                </span>
                            </div>
                            <ProgressBar
                                value={percentage}
                                showValue={false}
                                color={severity === 'danger' ? '#EF4444' : severity === 'warning' ? '#F59E0B' : '#10B981'}
                             aria-label="Progress" />
                            {percentage >= 90 && stats.limit !== null && (
                                <div className="text-xs text-red-500 mt-1">
                                    <i className="pi pi-exclamation-triangle mr-1"></i>
                                    Approaching limit
                                </div>
                            )}
                        </div>
                    );
                })}

                {features.length > 5 && (
                    <div className="text-center text-sm text-600 mt-2">
                        + {features.length - 5} more features
                    </div>
                )}
            </div>
        </Card>
    );
};

export default UsageWidget;
